import React, { useState, useCallback, useContext, useEffect } from 'react';
import { IEditorProps } from './Editor';
import './ActiveDirectoryPickerEditor.less';
import { startsWith } from '../../utils/StringUtils';
import { getChildValueAsArray } from 'components/property-editors/utils/ProviderNodeUtils';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { EditorLabel } from './EditorLabel';
import classNames from 'classnames';

interface IActiveDirectoryPickerEditorProps extends IEditorProps {}

export function ActiveDirectoryPickerEditor({ child, id, path }: IActiveDirectoryPickerEditorProps) {
  const [items, setItems] = useState<string[]>([]);
  const [selectedItem, setSelectedItem] = useState<string>();
  const context = useContext(PropertyEditorContext);

  useEffect(() => {
    setItems(getChildValueAsArray(child));
  }, [child]);

  const updateValue = useCallback(
    (selected: string[]) => {
      setItems(selected);
      context.onChange(selected, path);
    },
    [context, path]
  );

  const onAddClickHandler = useCallback(() => {
    context.onShowDialogue({
      node: child,
      path,
      editingContextId: context.editingContextId,
      options: {
        editingContextId: context.editingContextId,
        inputList: [...items],
        onClose: updateValue,
      },
    });
  }, [child, path, context, updateValue, items]);

  const onRemoveClickHandler = useCallback(() => {
    if (selectedItem) {
      const index = items.indexOf(selectedItem);
      if (index !== -1) {
        const newItems = [...items];
        newItems.splice(index, 1);
        updateValue(newItems);

        // select previous row
        const prevItem = newItems[index];
        if (prevItem) {
          setSelectedItem(prevItem);
        }
      }
    }
  }, [items, updateValue, selectedItem]);

  const selectRow = useCallback(
    (item: string) => {
      const index = items.indexOf(item);
      setSelectedItem(items[index]);
    },
    [items]
  );

  const getItemClassNames = useCallback(
    (item: string) => {
      return classNames('dp-ad-picker-editor__item', {
        'dp-ad-picker-editor__item--selected': item === selectedItem,
      });
    },
    [selectedItem]
  );

  const itemsList = (items || []).map((item, index) => (
    <li className={getItemClassNames(item)} onClick={() => selectRow(item)} key={index}>
      {getItemDisplayName(item)}
    </li>
  ));

  return (
    <EditorLabel editor={child} path={path} doNotWrap={true}>
      <div className="dp-ad-picker-editor">
        <div className="dp-ad-picker-editor__items-container">
          <ul className="dp-ad-picker-editor__items-list">{itemsList}</ul>
        </div>
        <div className="dp-ad-picker-editor__buttons-conatiner">
          <button className="dp-ad-picker-editor__add-button dp-button normal" onClick={onAddClickHandler}>
            Add
          </button>
          <button className="dp-ad-picker-editor__remove-button dp-button normal" onClick={onRemoveClickHandler}>
            Remove
          </button>
        </div>
      </div>
    </EditorLabel>
  );
}

function getItemDisplayName(folderPath: string) {
  const OUNIT = /OU=/g;
  const folderValue = folderPath.split(',');
  if (folderValue.length > 0) {
    let filteredFolders: string[] = [];
    if (startsWith(folderValue[0], 'OU=', true)) {
      filteredFolders = folderValue.filter(str => startsWith(str, 'OU=', true));
    } else {
      // this is a group
      // we might need a better way to check if this is a group or OU.
      filteredFolders.push(folderValue[0].split('=')[1]);
    }
    return filteredFolders
      .reverse()
      .map(val => val.replace(OUNIT, ''))
      .join('/');
  }
  return '';
}
